import React, { useState, useCallback } from 'react';
import { Button, Checkbox, Tab, TabList } from '@fluentui/react-components';
import { NotepadPersonRegular } from '@fluentui/react-icons';
import { Breadcrumbs } from '#root/components/Breadcrumbs';
import Paginator from '#root/components/Paginator';
import { Responder, useResponders } from "#root/queries/responders";
import { RESPONDER_STATUS } from "#root/utils/responders";
import ResponderCard from "#root/components/ResponderCard";
import LoadingWrapper from '#root/components/LoadingWrapper';
import ResponderFilters, { FilterState, initialFilters } from '#root/components/ResponderFilters';
import './styles.scss';

interface RespondersListProps {
  title: string;
  status?: number;
  renderActions: (responder: Responder, onActionComplete?: () => void) => React.ReactNode;
  renderFooterActions?: (selectedResponderIds: string[], onActionComplete?: () => void) => React.ReactNode;
  className?: string;
  showDeploymentFields?: boolean;
  selectEnabled?: boolean;
  isDeployable?: boolean;
  titleButton?: React.ReactNode;
  onIncidentChange?: (incidentId: string | undefined) => void;
}

const RespondersList: React.FC<RespondersListProps> = ({
  title,
  status,
  renderActions,
  renderFooterActions,
  className = '',
  showDeploymentFields = false,
  selectEnabled = true,
  isDeployable = false,
  titleButton,
  onIncidentChange 
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedResponders, setSelectedResponders] = useState<Set<string>>(new Set());
  const [checkedInSelected, setCheckedInSelected] = useState<boolean | undefined>(isDeployable ? true : undefined);

  const [filters, setFilters] = useState<FilterState>(() => ({
    ...initialFilters
  }));

  const {
    data: respondersResponse,
    isLoading,
    refetch
  } = useResponders({
    skip: (currentPage - 1) * itemsPerPage,
    take: itemsPerPage,
    ...filters,
    status: checkedInSelected === false ? RESPONDER_STATUS.DEMOBILIZED : (filters.status || status),
    isDeployable: checkedInSelected === false ? false : isDeployable
  });

  const totalCount = respondersResponse?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const responders = respondersResponse?.items ?? [];

  const handleItemsPerPageChange = (newItemsPerPage: number): void => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    refetch();
  };

  const handleCheckedInChange = (newCheckedInSelected: boolean) => {
    setCheckedInSelected(newCheckedInSelected);
    setCurrentPage(1);
    // Clear status filter when switching to Checked In tab
    if (newCheckedInSelected && filters.status) {
      handleFilterChange('status', undefined);
    }
    refetch();
  };

  const onSelectionChange = (event: any, data: any) => {
    const responderId = data.selectedItems[0];
    setSelectedResponders(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(responderId)) {
        newSelected.delete(responderId);
      } else {
        newSelected.add(responderId);
      }
      return newSelected;
    });
  };

  const getSelectionState = () => {
    if (selectedResponders.size === 0) return false;
    if (selectedResponders.size === responders.length) return true;
    return 'mixed';
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedResponders(new Set(responders.map(responder => responder.id!)));
    } else {
      setSelectedResponders(new Set());
    }
  };

  // Debounced filter change to prevent UI flicker
const handleFilterChange = useCallback((field: keyof FilterState, value: any) => {
  setTimeout(() => {
    setFilters(prev => ({
      ...prev,
      [field]: value === '' ? undefined : value
    }));
    setCurrentPage(1);
    if (field === 'incidentId' && onIncidentChange) {
      onIncidentChange(value === '' ? undefined : value);
    }
  }, 50);
}, [onIncidentChange]);



  const handleRefetch = () => {
    refetch();
  };

  return (
    <div className={`responders-list ${className}`}>
      <div className="emc-page-header">
        <div className="emc-page-header__top-row">
          <div className="emc-page-header__breadcrumbs">
            <Breadcrumbs />
          </div>
        </div>
        <div className="emc-page-header__title">
          <h2>{title} <div className="divider" />
            <div className="emc-page-header__title">  
              {titleButton}  
            </div>  
          </h2>
        </div>
      </div>

      <ResponderFilters
        filters={filters}
        statusFilterEnabled={checkedInSelected}
        onFilterChange={handleFilterChange}
        className="responders-list__filters"
      />

      {isDeployable && (
        <TabList className="responders-list__tab-list"
          selectedValue={checkedInSelected?.toString()}
          onTabSelect={(_, data) => handleCheckedInChange(data.value === 'true')}
        >
          <Tab value="true">Checked In</Tab>
          <Tab value="false">Demobilized</Tab>
        </TabList>
      )}

      <div className="responders-list__content">
        <LoadingWrapper
          isLoading={isLoading}
          isEmpty={responders.length === 0}
          emptyMessage={`No responders found`}
          emptyIcon={<NotepadPersonRegular />}
        >
          <div>
            <div className="responders-list__items">
              {responders.map((responder) => (
                <ResponderCard
                  key={responder.id}
                  responder={responder}
                  handleSelectionChange={onSelectionChange}
                  responderChecked={selectedResponders.has(responder.id!)}
                  actions={renderActions(responder, handleRefetch)}
                  showDeploymentFields={showDeploymentFields}
                  allowSelect={selectEnabled}
                />
              ))}
              <Paginator
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                totalItems={totalCount}
                currentItems={(currentPage - 1) * itemsPerPage + 1}
                showItemsPerPage={true}
              />
            </div>
          </div>
        </LoadingWrapper>
      </div>

      {selectEnabled && selectedResponders.size > 0 && renderFooterActions && (
        <div className="emc-page-footer">
          <Checkbox
            checked={getSelectionState()}
            onChange={(_, data) => handleSelectAll(!!data.checked)}
            label="Select All"
          />
          <Button
            appearance="secondary"
            onClick={() => setSelectedResponders(new Set())}
          >
            Cancel
          </Button>

          {renderFooterActions(Array.from(selectedResponders), handleRefetch)}
        </div>
      )}
    </div>
  );
};

export default RespondersList;
